import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, History, LogOut, Plus, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { StatusBadge } from '../components/UI/StatusBadge';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SelfServiceWorklistSkeleton from '../components/UI/SelfServiceWorklistSkeleton';
import FormSelect from '../components/UI/FormSelect';
import DatePickerField from '../components/UI/DatePickerField';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import ConfirmModal from '../components/UI/ConfirmModal';
import EventTimeline from '../components/UI/EventTimeline';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  useTerminationCapabilities,
  useTerminationRequests,
  useTerminationRequest,
  useEligibleTerminationWorkers,
  useCreateAdhocTermination,
  useRecordWorkerIntent,
  useCorrectNotice,
  useDepartmentAckTermination,
  useDepartmentReturnTermination,
  useCancelTermination,
} from '../hooks/useTerminationRequests';
import { formatDate, parseLocalDate, toLocalISODate } from '../utils/dateUtils';

const REASON_OPTIONS = [
  { value: 'performance', label: 'Performance' },
  { value: 'misconduct', label: 'Misconduct' },
  { value: 'redundancy', label: 'Redundancy / no longer required' },
  { value: 'contract_end', label: 'Contract / project end' },
  { value: 'medical', label: 'Medical' },
  { value: 'other', label: 'Other' },
];

const EVENT_LABELS = {
  created: 'Request created',
  worker_acknowledged: 'Worker acknowledged',
  disputed: 'Worker disputed',
  notice_corrected: 'Notice corrected',
  department_acknowledged: 'Department acknowledged',
  returned_for_correction: 'Returned for correction',
  approved: 'HR approved',
  rejected: 'HR rejected',
  cancelled: 'Cancelled',
};

const emptyForm = () => ({
  mode: 'adhoc',
  worker_staff_id: '',
  reason: '',
  reason_detail: '',
  notice_date: null,
  proposed_last_working_day: null,
});

const SelfServiceTerminationRequests = () => {
  const { canAccessAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const tab = searchParams.get('tab') || 'open';
  const isCompact = useIsMobile(1024);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [correctForm, setCorrectForm] = useState({ notice_date: null, proposed_last_working_day: null, reason_detail: '' });
  const [listHydrated, setListHydrated] = useState(false);

  const { data: caps } = useTerminationCapabilities();
  const { data: list = [], isLoading, isError, refetch } = useTerminationRequests(tab);
  const { data: detail, isLoading: detailLoading } = useTerminationRequest(selectedId);
  const { data: workers = [] } = useEligibleTerminationWorkers({ enabled: showNew });
  const createAdhoc = useCreateAdhocTermination();
  const recordIntent = useRecordWorkerIntent();
  const deptAck = useDepartmentAckTermination();
  const deptReturn = useDepartmentReturnTermination();
  const cancel = useCancelTermination();
  const correctNotice = useCorrectNotice();

  useEffect(() => {
    if (!isLoading) {
      setListHydrated(true);
    }
  }, [isLoading]);

  const openDetail = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next);
  };

  const closeCompactDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next);
  };

  const changeTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    next.delete('id');
    setSearchParams(next);
  };

  const toIso = (value) => toLocalISODate(value) || '';

  const submitNew = () => {
    if (form.mode === 'intent') {
      recordIntent.mutate({
        worker_staff_id: form.worker_staff_id,
        notice_date: toIso(form.notice_date),
        proposed_last_working_day: toIso(form.proposed_last_working_day),
        reason_detail: form.reason_detail,
      }, { onSuccess: () => { setShowNew(false); setForm(emptyForm()); } });
      return;
    }
    createAdhoc.mutate({
      worker_staff_id: form.worker_staff_id,
      reason: form.reason,
      reason_detail: form.reason_detail,
      proposed_last_working_day: toIso(form.proposed_last_working_day),
    }, { onSuccess: () => { setShowNew(false); setForm(emptyForm()); } });
  };

  const actions = detail?.allowed_actions || [];
  const originReadOnly = detail?.source === 'work_pass_end';

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <LogOut className="w-5 h-5 text-blue-800" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Termination Requests</h1>
            </div>
            <p className="text-sm text-gray-500 ml-12">
              Request a worker termination or record that a worker chose to leave
            </p>
          </div>
          {caps?.can_submit_termination && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowNew(true)}
            >
              <Plus className="w-4 h-4" />
              New request
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {['open', 'history'].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => changeTab(item)}
              className={`tab-btn ${tab === item ? 'active' : ''}`}
            >
              {item === 'open' ? 'Open' : 'History'}
            </button>
          ))}
        </div>

        {isLoading && !listHydrated ? (
          <SelfServiceWorklistSkeleton />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : isError ? (
          <div className="bg-white rounded-2xl border border-gray-200">
            <div className="text-center py-12 px-6">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load requests</h3>
              <p className="text-sm text-gray-500">Try again. Nothing on this page was lost.</p>
              <button type="button" className="btn btn-primary mt-4" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6">
            <div className={isCompact && selectedId ? 'hidden' : 'space-y-3'}>
              {list.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200">
                  <div className="text-center py-12 px-6">
                    {tab === 'history' ? (
                      <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    ) : (
                      <LogOut className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {tab === 'history' ? 'No closed requests yet' : 'No open requests'}
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm mx-auto">
                      {tab === 'history'
                        ? 'Approved, rejected, or cancelled requests from this portal will appear here.'
                        : 'Start a request when a worker needs to leave, or when they have already chosen to leave.'}
                    </p>
                    {tab === 'history' && canAccessAdmin() && (
                      <Link
                        to="/admin/employee-lifecycle?tab=termination-requests&view=legacy"
                        className="btn btn-outline mt-4"
                      >
                        Open HR historical archive
                      </Link>
                    )}
                    {tab !== 'history' && caps?.can_submit_termination && (
                      <button
                        type="button"
                        className="btn btn-primary mt-4"
                        onClick={() => setShowNew(true)}
                      >
                        <Plus className="w-4 h-4" />
                        New request
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                list.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openDetail(item.id)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      selectedId === item.id ? 'border-blue-800 shadow-md' : 'border-gray-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.worker_name_snapshot}</p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {item.request_number} · {item.worker_staff_id}
                        </p>
                      </div>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 tabular-nums">
                      Proposed last day {formatDate(item.proposed_last_working_day)}
                    </p>
                  </button>
                ))
              )}
            </div>

            <div className={isCompact && !selectedId ? 'hidden' : ''}>
              {isCompact && selectedId && (
                <button type="button" onClick={closeCompactDetail} className="btn btn-outline mb-3">
                  <ArrowLeft className="w-4 h-4" />
                  Back to list
                </button>
              )}
              {!selectedId ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
                  Select a request to review it
                </div>
              ) : detailLoading || !detail ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4 lg:sticky lg:top-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{detail.worker_name_snapshot}</h2>
                      <p className="text-sm text-gray-500">{detail.request_number}</p>
                    </div>
                    <StatusBadge status={detail.status} />
                  </div>
                  {originReadOnly && (
                    <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-xl p-3">
                      This request was generated from an approved work-pass end-at-expiry decision. The worker, reason, and date cannot be re-entered.
                    </p>
                  )}
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs text-gray-500">Source</dt>
                      <dd className="font-medium text-gray-900">{detail.source.replace(/_/g, ' ')}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Proposed last day</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.proposed_last_working_day)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Notice date</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.notice_date)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Requested by</dt>
                      <dd className="font-medium text-gray-900">{detail.requested_by_name_snapshot || '—'}</dd>
                    </div>
                  </dl>

                  {actions.includes('department_acknowledge') && (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        className="btn btn-primary flex-1"
                        onClick={() => setConfirm({ kind: 'dept_ack' })}
                      >
                        Acknowledge notice
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline flex-1"
                        onClick={() => setConfirm({ kind: 'dept_return' })}
                      >
                        Return for correction
                      </button>
                    </div>
                  )}
                  {actions.includes('correct_notice') && (
                    <button
                      type="button"
                      className="btn btn-primary w-full"
                      onClick={() => setConfirm({ kind: 'correct' })}
                    >
                      Correct notice dates
                    </button>
                  )}
                  {actions.includes('cancel') && (
                    <button
                      type="button"
                      className="btn btn-outline w-full"
                      onClick={() => setConfirm({ kind: 'cancel' })}
                    >
                      Cancel request
                    </button>
                  )}

                  <EventTimeline
                    events={(detail.events || []).map((event) => ({
                      id: event.id,
                      label: EVENT_LABELS[event.event_type] || event.event_type,
                      subtitle: [event.actor_name, event.reason].filter(Boolean).join(' · '),
                      timestamp: event.created_at,
                      color: event.event_type === 'rejected' || event.event_type === 'disputed' ? 'red' : 'blue',
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal-container modal-container-sm" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New request</h3>
              <button type="button" className="modal-close" onClick={() => setShowNew(false)} aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-content space-y-4">
              <div className="flex gap-2">
                {[
                  { id: 'adhoc', label: 'End employment' },
                  { id: 'intent', label: 'Worker chose to leave' },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    className={`tab-btn ${form.mode === mode.id ? 'active' : ''}`}
                    onClick={() => setForm((current) => ({ ...current, mode: mode.id }))}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <SearchableCombobox
                variant="form"
                theme="employee"
                items={workers}
                value={form.worker_staff_id}
                onChange={(value) => setForm((current) => ({ ...current, worker_staff_id: value }))}
                placeholder="Select worker"
                displayFn={(worker) => `${worker.name} · ${worker.staff_id}`}
                valueFn={(worker) => worker.staff_id}
                searchFn={(worker, term) => `${worker.name} ${worker.staff_id}`.toLowerCase().includes(term)}
              />
              {form.mode === 'adhoc' ? (
                <FormSelect
                  theme="employee"
                  options={REASON_OPTIONS}
                  value={form.reason}
                  onChange={(value) => setForm((current) => ({ ...current, reason: value }))}
                  placeholder="Reason"
                />
              ) : (
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notice date</span>
                  <DatePickerField
                    selected={form.notice_date ? parseLocalDate(toIso(form.notice_date)) : null}
                    onChange={(value) => setForm((current) => ({ ...current, notice_date: value }))}
                    theme="employee"
                  />
                </div>
              )}
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Proposed last working day</span>
                <DatePickerField
                  selected={form.proposed_last_working_day ? parseLocalDate(toIso(form.proposed_last_working_day)) : null}
                  onChange={(value) => setForm((current) => ({ ...current, proposed_last_working_day: value }))}
                  theme="employee"
                />
              </div>
              <textarea
                className="input w-full"
                rows={3}
                placeholder="Optional factual note"
                value={form.reason_detail}
                onChange={(event) => setForm((current) => ({ ...current, reason_detail: event.target.value }))}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setShowNew(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!form.worker_staff_id || !form.proposed_last_working_day || (form.mode === 'adhoc' && !form.reason) || (form.mode === 'intent' && !form.notice_date) || createAdhoc.isPending || recordIntent.isPending}
                onClick={submitNew}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirm?.kind === 'dept_ack'}
        theme="employee"
        variant="primary"
        title="Acknowledge this resignation notice"
        confirmLabel="Acknowledge"
        isLoading={deptAck.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => deptAck.mutate({ id: selectedId, data: {} }, { onSuccess: () => setConfirm(null) })}
      >
        <p className="text-sm text-gray-700">
          This acknowledges the dates and facts. It does not decide whether the worker may resign.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'dept_return'}
        theme="employee"
        variant="danger"
        title="Return for a date or fact correction"
        confirmLabel="Return"
        confirmDisabled={!returnReason.trim()}
        isLoading={deptReturn.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => deptReturn.mutate(
          { id: selectedId, data: { reason: returnReason } },
          { onSuccess: () => { setConfirm(null); setReturnReason(''); } },
        )}
      >
        <textarea
          className="input w-full"
          rows={3}
          value={returnReason}
          onChange={(event) => setReturnReason(event.target.value)}
          placeholder="What needs to be corrected?"
        />
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'correct'}
        theme="employee"
        variant="primary"
        title="Correct notice dates"
        confirmLabel="Send corrected notice"
        confirmDisabled={!correctForm.notice_date || !correctForm.proposed_last_working_day}
        isLoading={correctNotice.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => correctNotice.mutate(
          {
            id: selectedId,
            data: {
              notice_date: toIso(correctForm.notice_date),
              proposed_last_working_day: toIso(correctForm.proposed_last_working_day),
              reason_detail: correctForm.reason_detail,
            },
          },
          { onSuccess: () => setConfirm(null) },
        )}
      >
        <div className="space-y-3">
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notice date</span>
            <DatePickerField
              selected={correctForm.notice_date}
              onChange={(value) => setCorrectForm((current) => ({ ...current, notice_date: value }))}
              theme="employee"
            />
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Proposed last working day</span>
            <DatePickerField
              selected={correctForm.proposed_last_working_day}
              onChange={(value) => setCorrectForm((current) => ({ ...current, proposed_last_working_day: value }))}
              theme="employee"
            />
          </div>
          <textarea
            className="input w-full"
            rows={2}
            placeholder="Optional factual note"
            value={correctForm.reason_detail}
            onChange={(event) => setCorrectForm((current) => ({ ...current, reason_detail: event.target.value }))}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'cancel'}
        theme="employee"
        variant="danger"
        title="Cancel this request"
        confirmLabel="Cancel request"
        isLoading={cancel.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => cancel.mutate({ id: selectedId, data: {} }, { onSuccess: () => setConfirm(null) })}
      >
        <p className="text-sm text-gray-700">The request will be withdrawn from HR review.</p>
      </ConfirmModal>
    </DashboardLayout>
  );
};

export default SelfServiceTerminationRequests;
